import React, { useCallback, useEffect, useRef, useState } from 'react';

// Internal Imports
import DataGrid from '../../common/components/DataGrid/DataGrid';
import MenuLayout from '../../common/components/MenuLayout/MenuLayout';
import ConfirmationModal from '../../common/components/Modal/CorfimationModal';
import Modal from '../../common/components/Modal/Modal';
import Paint from '../../common/components/Paint/Paint';
import { COLORS } from '../../common/constants/colors';
import { getError, openNotification, tryCatch } from '../../common/constants/Utilities';
import { Mode } from '../../common/types/AllTypes';
import ViewContainer from '../../common/components/ViewContainer/ViewContainer';
import { useForm } from '../../contexts/form';
import { useListDepartmentByUer } from '../../hooks/useListDepartment';
import IColumn from '../../interfaces/IColumn';
import { IListBase } from '../../interfaces/IListado';
import { IMenu } from '../../interfaces/IMenu';
import { DeactiveListBaseById, GetListBase, GetListBaseByListID } from '../../services/list';
import AddUpdateListadoClientes from './AddUpdateListadoClientes';
import './ListadoClientes.css';
import { LoadingIcon } from '../../common/constants/cssElements';

interface Props {
    menu: IMenu;
}

const ListadoClientes: React.FC<Props> = ({ menu }) => {

    // States
    const { listDepartment, loadingDeparment } = useListDepartmentByUer();

    const { setListBaseForm, listBaseForm } = useForm();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingAction, setLoadingAction] = useState<boolean>(false);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [mode, setMode] = useState<Mode>('Add');
    const [searchV, setSearchV] = useState<string>('');
    const [list_ListBase, setList_ListBase] = useState<IListBase[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    
    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [idDetail, setIdDetail] = useState<number | null>(null);

    const groupDep = useRef<IListBase>();

    // Consts
    const columns: IColumn[] = [
        {
            title: 'Listado',
            dataIndex: 'nombre',
        },
        {
            title: 'Tipo',
            dataIndex: 'listadoTipoNombre',
        },
        {
            title: 'Departamento',
            dataIndex: 'departamentoNombre',
        },
        {
            title: 'Última Actulización',
            dataIndex: '',
            render: (form: IListBase) => !form.fechaUltimaActualizacion ? '' : new Date(form.fechaUltimaActualizacion!).toLocaleString()
        },
        {
            title: 'Proxima Actulización',
            dataIndex: '',
            render: (form: IListBase) => !form.fechaProximaActualizacion ? '' : new Date(form.fechaProximaActualizacion!).toLocaleString()
        },
        {
            title: <i className="fa-solid fa-rotate button_refresh" onClick={() => { handleGetListBase(); setSearchV('') }}></i>,
            dataIndex: '',
            render: (data: IListBase) => loadingDetail && idDetail === data.listadoID ? <LoadingIcon color={COLORS.blue_active} size={'20px'} /> : <></>
        }
    ];

    // handles
    const handleGetListBase = () => {
        tryCatch(() => {
            if (!!listDepartment) {
                setLoading(true);
                GetListBase()
                .then((response => {

                    const listListBase: IListBase[] = response.data;
                    listListBase.forEach(qtGroup => {
                        qtGroup.departamentoNombre = listDepartment.find(dep => dep.departamentoGUID === qtGroup.departamentoGUID)?.nombre;
                    })

                    setList_ListBase(listListBase);
                }))
                .catch((response) => {
                    openNotification('error', 'Error', getError(response?.msg));
                })
                .finally(() => setLoading(false));
            }
        })
    }


    const handleGetListBaseByListID = (listBase: IListBase) => {
        setListBaseForm(undefined);
        groupDep.current = undefined;

        if (!!listBase) {

            setIdDetail(listBase.listadoID);
            setLoadingDetail(true);

            tryCatch(() => {
                GetListBaseByListID(listBase.listadoID)
                    .then(({ data }) => {
                        setListBaseForm(data);
                        groupDep.current = listBase;
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoadingDetail(false))
            })
        }
        
    }

    const handleDeactiveListBaseByID = () => {
        tryCatch(() => {
            if (!!listBaseForm) {
                setLoadingAction(true);
                DeactiveListBaseById(listBaseForm?.listadoID!)
                    .then((response) => {
                        openNotification('success', `Listado (${listBaseForm.nombre}) eliminado`, response.msg!);
                        setList_ListBase(list_ListBase.filter(listB => listB.listadoID !== listBaseForm.listadoID));
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => {
                        setLoadingAction(false);
                        setDeleteModal(false);
                        setListBaseForm(undefined);
                    })
            }
        })
    }


    // Methods
    const getListBaseFiltered = useCallback((): IListBase[] => {
        let listBaseFiltered: IListBase[] = [];
        const valueLowerCase = searchV.toLowerCase();

        if (!!valueLowerCase) {
            listBaseFiltered = list_ListBase.filter((list) => list.nombre?.toLowerCase().includes(valueLowerCase) || list.listadoTipoNombre?.toLowerCase().includes(valueLowerCase) || list.departamentoNombre?.toLowerCase().includes(valueLowerCase) || list.fechaUltimaActualizacion?.toLowerCase().includes(valueLowerCase) || list.fechaProximaActualizacion?.toLowerCase().includes(valueLowerCase))
        } else {
            listBaseFiltered = list_ListBase;
        }

        return listBaseFiltered;
    }, [searchV, list_ListBase]);

    const openModalUpdate = () => {
        if (!!!groupDep.current?.departamentoNombre) {
            openNotification('warning', `Este Listado tiene el Departamento Deshabilitado`, 'Contáctese con el equipo Bis para más información.');
        } else {
            setMode('Update');
            setAddUpdateModal(true);
        }
    }

    const openModalCreate = () => {
        setMode('Add');
        setListBaseForm(undefined);
        setAddUpdateModal(true);
    }

    //Effects
    useEffect(() => {
        handleGetListBase();
    }, [, listDepartment]);

    useEffect(() => {
        if (!!!addUpdateModal) {
            setSelectedRowKeys([]);
            setListBaseForm(undefined);
        }
    }, [addUpdateModal]);

    return (
        <MenuLayout currentPage={menu.nombre}>
            <ViewContainer>
                <DataGrid
                    searchV={searchV}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    gridProps={{ rowKey: (record: any) => record.listadoID + record.nombre }}
                    columns={columns}
                    searchMethod={setSearchV}
                    menu={menu}
                    loading={loading}
                    dataSource={getListBaseFiltered()}
                    setItemSelected={handleGetListBaseByListID}
                    addAction={{ active: true, method: openModalCreate }}
                    deleteAction={{ active: !!listBaseForm, method: () => setDeleteModal(true) }}
                    updateAction={{ active: !!listBaseForm, method: openModalUpdate }}
                />
            </ViewContainer>


            <ConfirmationModal
                title={menu.nombre}
                visible={deleteModal}
                setVisible={setDeleteModal}
                question="¿Seguro que deseas eliminar este Listado?"
                method={handleDeactiveListBaseByID}
                loading={loadingAction}
            />

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {mode === 'Add' ? 'Crear' : 'Modificar'}  <Paint color={COLORS.blue_main(1)}>Listado</Paint></nav>} visible={addUpdateModal} setVisible={setAddUpdateModal} askBeforeClose={true}>
                <AddUpdateListadoClientes dataList={list_ListBase} setDataList={setList_ListBase} mode={mode} setMode={setMode} setAddUpdateModal={setAddUpdateModal} listDepartment={listDepartment} loadingDeparment={loadingDeparment} />
            </Modal>
        </MenuLayout>
    );
}

export default ListadoClientes;